/*!

 * TEMA DESENVOLVIDO POR SABINOv LTDA.

 * O USO INDEVIDO DO TEMA RESULTARÁ EM DMCA POR DIREITOS DO TEMA

 * @license Proprietary

 * @preserve

 */



import { Component } from '@theme/component';

import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';

import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';

import { cartPerformance } from '@theme/performance';

import { morph } from '@theme/morph';



export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 300;



/**

 * A custom element that manages an add to cart button.

 *

 * @typedef {object} AddToCartRefs

 * @property {HTMLButtonElement} addToCartButton - The add to cart button.

 * @extends Component<AddToCartRefs>

 */

export class AddToCartComponent extends Component {

  requiredRefs = ['addToCartButton'];



  /** @type {number | undefined} */

  #animationTimeout;



  /** @type {number | undefined} */

  #cleanupTimeout;



  connectedCallback() {

    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);

  }



  disconnectedCallback() {

    super.disconnectedCallback();



    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);

    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    this.removeEventListener('pointerenter', this.#preloadImage);

  }



  disable() {

    this.refs.addToCartButton.disabled = true;

  }



  enable() {

    this.refs.addToCartButton.disabled = false;

  }



  /**

   * Handles the click event for the add to cart button.

   * @param {MouseEvent & {target: HTMLElement}} event

   */

  handleClick(event) {



  this.animateAddToCart();



}



  #preloadImage = () => {

    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);

  };







  animateAddToCart() {

    const { addToCartButton } = this.refs;



    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);

    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);



    if (!addToCartButton.classList.contains('atc-added')) {

      addToCartButton.classList.add('atc-added');

    }



    this.#animationTimeout = setTimeout(() => {

      this.#cleanupTimeout = setTimeout(() => {

        this.refs.addToCartButton.classList.remove('atc-added');

      }, 10);

    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);

  }

}



if (!customElements.get('add-to-cart-component')) {

  customElements.define('add-to-cart-component', AddToCartComponent);

}



/**

 * A custom element that manages a product form.

 *

 * @typedef {object} ProductFormRefs

 * @property {HTMLInputElement} variantId

 * @property {AddToCartComponent | undefined} addToCartButtonContainer

 * @property {HTMLElement | undefined} addToCartTextError

 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer

 * @property {HTMLElement} liveRegion

 *

 * @extends Component<ProductFormRefs>

 */

class ProductFormComponent extends Component {

  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();



  /** @type {number | undefined} */

  #timeout;



  connectedCallback() {

    super.connectedCallback();



    const { signal } = this.#abortController;

    const target = this.closest('.shopify-section, dialog, product-card');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

  }



  disconnectedCallback() {

    super.disconnectedCallback();

    this.#abortController.abort();

  }



  /**

   * Handles the submit event for the product form.

   *

   * @param {Event} event

   */

  handleSubmit(event) {

    const { addToCartTextError } = this.refs;



    event.preventDefault();



    if (this.#timeout) clearTimeout(this.#timeout);



    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;



    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');



    const formData = new FormData(form);

    /*
     * Pede ao Shopify a section atualizada do carrinho na MESMA
     * requisição do /cart/add. Isso evita uma segunda ida ao servidor
     * antes de abrir o drawer.
     */
    const cartItemsComponent = document.querySelector('cart-items-component');
    const cartSectionId = cartItemsComponent?.dataset.sectionId;

    if (cartSectionId) {
      formData.append('sections', cartSectionId);
      formData.append('sections_url', window.location.pathname);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });



    



    fetch(Theme.routes.cart_add_url, {

      ...fetchCfg,

      headers: {

        ...fetchCfg.headers,

        Accept: 'application/json',

      },

    })

      .then((response) => response.json())

      .then(async (response) => {

        if (response.status) {

          window.dispatchEvent(new CartErrorEvent(this.id, response.message));



          if (!addToCartTextError) return;

          addToCartTextError.classList.remove('hidden');



          const textNode = addToCartTextError.childNodes[2];

          if (textNode) {

            textNode.textContent = response.message;

          } else {

            const newTextNode = document.createTextNode(response.message);

            addToCartTextError.appendChild(newTextNode);

          }



          this.#setLiveRegionText(response.message);



          this.#timeout = setTimeout(() => {

            if (!addToCartTextError) return;

            addToCartTextError.classList.add('hidden');

            this.#clearLiveRegionText();

          }, 10000);



          this.dispatchEvent(

            new CartAddEvent({}, this.id, {

              didError: true,

              source: 'product-form-component',

              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),

              productId: this.dataset.productId,

            })

          );



          return;

        }



        const id = formData.get('id');

        if (!id) throw new Error('Form ID is required');



        if (addToCartTextError) {

          addToCartTextError.classList.add('hidden');

          addToCartTextError.removeAttribute('aria-live');

        }



        if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {

          const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;

          const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');

          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;



          this.#setLiveRegionText(addedText);



          setTimeout(() => {

            this.#clearLiveRegionText();

          }, 5000);

        }



        /*
         * Caminho rápido:
         * o próprio /cart/add já pode devolver a section atualizada.
         * Assim não precisamos fazer uma segunda requisição antes de abrir.
         */
        let drawerUpdated = false;

        if (
          cartSectionId &&
          response.sections &&
          response.sections[cartSectionId]
        ) {
          drawerUpdated = this.#applyCartDrawerSection(
            response.sections[cartSectionId]
          );
        }

        /*
         * Fallback de segurança:
         * se a section não veio ou não pôde ser aplicada, usa o refresh
         * antigo para não comprometer o funcionamento do carrinho.
         */
        if (!drawerUpdated) {
          await this.#refreshCartDrawer();
        }

        this.dispatchEvent(
          new CartAddEvent({}, id.toString(), {
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
            sections: response.sections || null,
          })
        );

      })

      .catch((error) => {

        console.error(error);

      })

      .finally(() => {

        cartPerformance.measureFromEvent('add:user-action', event);

      });

  }



  #applyCartDrawerSection(sectionHtml) {
    if (!sectionHtml) return false;

    try {
      const temp = document.createElement('div');
      temp.innerHTML = sectionHtml;

      const newCartItems = temp.querySelector('cart-items-component');
      const currentCartItems = document.querySelector('cart-items-component');

      if (!newCartItems || !currentCartItems) {
        return false;
      }

      currentCartItems.replaceWith(newCartItems);

      /*
       * Mantém os eventos que o tema e os scripts customizados já usam
       * para sincronizar preços, recomendações, contador etc.
       */
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );
      document.dispatchEvent(
        new CustomEvent('cart:updated', { bubbles: true })
      );
      window.dispatchEvent(new CustomEvent('cart:refresh'));
      window.dispatchEvent(new CustomEvent('cart:updated'));

      return true;
    } catch (error) {
      console.warn(
        'Não foi possível aplicar a section retornada pelo cart/add:',
        error
      );
      return false;
    }
  }


  async #refreshCartDrawer() {

    const cartItemsComponent = document.querySelector('cart-items-component');

    const sectionId = cartItemsComponent?.dataset.sectionId;



    if (!sectionId) return;



    try {

      const html = await fetch(`${window.location.pathname}?section_id=${sectionId}`, {

        credentials: 'same-origin',

      }).then((r) => r.text());



      const temp = document.createElement('div');

      temp.innerHTML = html;



      const newCartItems = temp.querySelector('cart-items-component');

      const currentCartItems = document.querySelector('cart-items-component');



      if (newCartItems && currentCartItems) {

        currentCartItems.replaceWith(newCartItems);

      }



      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

      window.dispatchEvent(new CustomEvent('cart:refresh'));

      window.dispatchEvent(new CustomEvent('cart:updated'));

    } catch (error) {

      console.warn('Erro ao atualizar cart drawer:', error);

    }

  }



  /**

   * @param {*} text

   */

  #setLiveRegionText(text) {

    const liveRegion = this.refs.liveRegion;

    liveRegion.textContent = text;

  }



  #clearLiveRegionText() {

    const liveRegion = this.refs.liveRegion;

    liveRegion.textContent = '';

  }



  /**

   * @param {VariantUpdateEvent} event

   */

  #onVariantUpdate = (event) => {

    if (event.detail.data.newProduct) {

      this.dataset.productId = event.detail.data.newProduct.id;

    } else if (event.detail.data.productId !== this.dataset.productId) {

      return;

    }



    const { variantId, addToCartButtonContainer } = this.refs;



    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;

    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');



    if (!currentAddToCartButton) return;



    if (event.detail.resource == null || event.detail.resource.available == false) {

      addToCartButtonContainer.disable();

      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');

    } else {

      addToCartButtonContainer.enable();

      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');

    }



    if (newAddToCartButton) {

      morph(currentAddToCartButton, newAddToCartButton);

    }



    variantId.value = event.detail.resource.id ?? '';



    if (event.detail.resource) {

      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;

      productVariantMedia &&

        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');

    }

  };



  #onVariantSelected = () => {

    this.refs.addToCartButtonContainer?.disable();

  };

}



if (!customElements.get('product-form-component')) {

  customElements.define('product-form-component', ProductFormComponent);

}



class FlyToCart extends HTMLElement {

  /** @type {Element} */

  source;



  /** @type {Element} */

  destination;



  connectedCallback() {

    this.#animate();

  }



  #animate() {

    const rect = this.getBoundingClientRect();

    const sourceRect = this.source.getBoundingClientRect();

    const destinationRect = this.destination.getBoundingClientRect();



    const offset = {

      x: rect.width / 2,

      y: rect.height / 2,

    };



    const startPoint = {

      x: sourceRect.left + sourceRect.width / 2 - offset.x,

      y: sourceRect.top + sourceRect.height / 2 - offset.y,

    };



    const endPoint = {

      x: destinationRect.left + destinationRect.width / 2 - offset.x,

      y: destinationRect.top + destinationRect.height / 2 - offset.y,

    };



    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };

    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };



    let startTime = null;

    const duration = 200;



    this.style.opacity = '1';



    const animate = (currentTime) => {

      if (!startTime) startTime = currentTime;

      const elapsed = currentTime - startTime;

      const progress = Math.min(elapsed / duration, 1);



      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);



      this.style.setProperty('--x', `${position.x}px`);

      this.style.setProperty('--y', `${position.y}px`);



      const scale = 1 - progress * 0.5;

      this.style.setProperty('--scale', `${scale}`);



      if (progress < 1) {

        requestAnimationFrame(animate);

      } else {

        this.style.opacity = '0';

        onAnimationEnd(this, () => this.remove());

      }

    };



    this.style.setProperty('--x', `${startPoint.x}px`);

    this.style.setProperty('--y', `${startPoint.y}px`);



    requestAnimationFrame(animate);

  }

}



/**

 * Calculates a point on a cubic Bézier curve.

 * @param {number} t

 * @param {{x: number, y: number}} p0

 * @param {{x: number, y: number}} p1

 * @param {{x: number, y: number}} p2

 * @param {{x: number, y: number}} p3

 * @returns {{x: number, y: number}}

 */

function bezierPoint(t, p0, p1, p2, p3) {

  const cX = 3 * (p1.x - p0.x);

  const bX = 3 * (p2.x - p1.x) - cX;

  const aX = p3.x - p0.x - cX - bX;



  const cY = 3 * (p1.y - p0.y);

  const bY = 3 * (p2.y - p1.y) - cY;

  const aY = p3.y - p0.y - cY - bY;



  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;

  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;



  return { x, y };

}



if (!customElements.get('fly-to-cart')) {

  customElements.define('fly-to-cart', FlyToCart);

}